import { useCallback } from 'react';
import { createGlobalState } from 'react-use/lib/factory/createGlobalState';

export type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N;
export type IsAny<T> = IfAny<T, true, false>;

type Callback<T> = IsAny<T> extends true
  ? (...param: any) => void
  : [T] extends [void]
  ? (...param: unknown[]) => void
  : [T] extends [any[]]
  ? (...param: T) => void
  : (...param: [T, ...unknown[]]) => void;

export type EventHookOn<T = any> = (fn: Callback<T>) => { off: () => void };
export type EventHookOff<T = any> = (fn: Callback<T>) => void;
export type EventHookTrigger<T = any> = (
  ...param: Parameters<Callback<T>>
) => Promise<unknown[]>;

export interface EventHook<T = any> {
  on: EventHookOn<T>;
  off: EventHookOff<T>;
  trigger: EventHookTrigger<T>;
}

const useEvents = createGlobalState<Record<string, Set<Callback<any>>>>({});

export const useEventHook = <T = any>({
  key,
}: {
  key: string;
}): EventHook<T> => {
  const [events, setEvents] = useEvents();

  const off = useCallback(
    (fn: Callback<T>) => {
      setEvents(events => {
        events[key] = events[key] ?? new Set<Callback<T>>();
        events[key].delete(fn);
        return events;
      });
    },
    [key, setEvents],
  );

  const on = useCallback(
    (fn: Callback<T>) => {
      setEvents(events => {
        events[key] = events[key] ?? new Set<Callback<T>>();
        events[key].add(fn);
        return events;
      });
      return {
        off: () => off(fn),
      };
    },
    [key, off, setEvents],
  );

  const trigger = useCallback(
    (...param: Parameters<Callback<T>>) => {
      return Promise.all(
        Array.from(events[key] ?? []).map(fn => fn(...(param as unknown[]))),
      );
    },
    [key, events],
  );

  return { on, off, trigger };
};
